// src/hooks/useFetchOrgTemplates.js

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Infinite‑scroll hook for *organization* templates
 * ──────────────────────────────────────────────────────────────
 *  ✦ Filters
 *      • isTemplate      == true
 *      • templateScope   == "organization"
 *      • organizationId  == user.orgId
 *
 *  ✦ Pagination
 *      • pageSize (default 20)
 *      • Firestore startAfter(lastDoc)
 *
 *  ✦ Optimistic refresh
 *      • 2nd arg `refreshKey` (default 0) lets the parent bump a counter
 *        to reset state & refetch immediately after a new template is published.
 *
 *  Usage example:
 *    const {
 *      orgTemplates,
 *      isLoading,
 *      hasMore,
 *      fetchNextBatch
 *    } = useFetchOrgTemplates(20, refreshTick);
 */

export function useFetchOrgTemplates(pageSize = 20, refreshKey = 0) {
  // ─────────────────────────────────────────────────────────
  //  CONTEXT
  // ─────────────────────────────────────────────────────────
  const { currentUser, organizationId } = useAuth();

  // ─────────────────────────────────────────────────────────
  //  STATE
  // ─────────────────────────────────────────────────────────
  const [orgTemplates, setOrgTemplates] = useState([]); // all loaded docs
  const [lastDoc,      setLastDoc]      = useState(null); // cursor for pagination
  const [isLoading,    setIsLoading]    = useState(false);
  const [hasMore,      setHasMore]      = useState(true);

  // Track prior values to detect changes
  const prevUserIdRef = useRef(null);
  const prevKeyRef    = useRef(refreshKey);

  // ─────────────────────────────────────────────────────────
  //  FETCH NEXT BATCH (callback so it can be reused)
  // ─────────────────────────────────────────────────────────
  const fetchNextBatch = useCallback(async () => {
    console.log('[useFetchOrgTemplates] fetchNextBatch called');

    // Guard clauses
    if (isLoading)           { console.log('[useFetchOrgTemplates] Already loading'); return; }
    if (!hasMore)            { console.log('[useFetchOrgTemplates] No more to load'); return; }
    if (!currentUser?.uid ||
        !organizationId)     { console.log('[useFetchOrgTemplates] Missing user/orgId'); return; }

    setIsLoading(true);
    console.log('[useFetchOrgTemplates] Setting isLoading = true');

    try {
      // Build Firestore query
      let baseQuery = query(
        collection(db, 'forms'),
        where('isTemplate', '==', true),
        where('templateScope', '==', 'organization'),
        where('organizationId', '==', organizationId),
        where('status', 'in', ['draft', 'published']),
        orderBy('publishedAt', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        console.log('[useFetchOrgTemplates] startAfter pagination doc:', lastDoc.id);
        baseQuery = query(baseQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(baseQuery);
      console.log('[useFetchOrgTemplates] Fetched', snapshot.size, 'docs');

      if (snapshot.empty) {
        console.log('[useFetchOrgTemplates] No more documents found');
        setHasMore(false);
        return;
      }

      const newTemplates = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log('[useFetchOrgTemplates] New templates:', newTemplates);

      if (newTemplates.length < pageSize) setHasMore(false);

      setOrgTemplates((prev) => {
        console.log('[useFetchOrgTemplates] Appending to state. Prev length:', prev.length);
        return [...prev, ...newTemplates];
      });

      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      console.log('[useFetchOrgTemplates] Updated lastDoc:', snapshot.docs[snapshot.docs.length - 1].id);
    } catch (err) {
      console.error('[useFetchOrgTemplates] ❌ Error:', err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      console.log('[useFetchOrgTemplates] Setting isLoading = false');
    }
  }, [
    isLoading,
    hasMore,
    lastDoc,
    currentUser?.uid,
    organizationId,
    pageSize
  ]);

  // ─────────────────────────────────────────────────────────
  //  RESET + FIRST LOAD on user change OR refresh tick
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    console.log('[useFetchOrgTemplates] useEffect triggered');
    console.log('  currentUser.uid :', currentUser?.uid);
    console.log('  organizationId  :', organizationId);
    console.log('  refreshKey      :', refreshKey);

    if (!currentUser?.uid || !organizationId) return;

    const userChanged      = prevUserIdRef.current !== currentUser.uid;
    const refreshKeyBumped = prevKeyRef.current   !== refreshKey;

    if (userChanged || refreshKeyBumped) {
      console.log('[useFetchOrgTemplates] Reset state → initial fetch');
      setOrgTemplates([]);
      setLastDoc(null);
      setHasMore(true);
      prevUserIdRef.current = currentUser.uid;
      prevKeyRef.current    = refreshKey;
      fetchNextBatch(); // kick off first load
    }
    // eslint‑disable‑next‑line react-hooks/exhaustive-deps
  }, [currentUser?.uid, organizationId, refreshKey, fetchNextBatch]);

  // ─────────────────────────────────────────────────────────
  //  PUBLIC API
  // ─────────────────────────────────────────────────────────
  return {
    orgTemplates,
    isLoading,
    hasMore,
    fetchNextBatch
  };
}
